import { getRouteApi } from '@tanstack/react-router'
import { Suspense } from 'react'
import { z } from 'zod'

import { lazySkelPage, lenientSkel, SkelPageLoading, ULID_RE } from '@/routes/skel.index'

/**
 * `/skel/split?id=<ulid>` — "Split to bones": one picture of a character -> a puppet with bones
 * (`features/skel/imagerig`). Without `id` the page starts with an upload; with it, it shows that
 * picture (choose how to split, progress, result). A malformed id is dropped (-> upload).
 *
 * From an existing character: `?asset=<catalog uid>` shows its own pictures per view, and
 * `?set=<ulid>` the set made from them (per-view progress and editing, coverage, Save). `set` wins
 * over `asset`, which wins over `id`.
 */

/** A catalog uid as the server mints them (`char:people/school_roles/woman_coach`, `obj:toys/ball`). */
export const ASSET_UID_RE = /^(char|obj):[A-Za-z0-9_./:-]{1,280}$/

export const skelSplitSearchSchema = z.object({
  id: lenientSkel(z.string().regex(ULID_RE)),
  asset: lenientSkel(z.string().regex(ASSET_UID_RE)),
  set: lenientSkel(z.string().regex(ULID_RE)),
})
export type SkelSplitSearch = z.infer<typeof skelSplitSearchSchema>

export function validateSkelSplitSearch(input: Record<string, unknown>): SkelSplitSearch {
  return skelSplitSearchSchema.parse(input)
}

export const SKEL_SPLIT_ROUTE_ID = '/skel/split' as const
const route = getRouteApi(SKEL_SPLIT_ROUTE_ID)

const SplitToBonesPage = lazySkelPage(() => import('@/features/skel/imagerig/SplitToBonesPage'), 'Split to bones page')

export function SkelSplitPage() {
  const { id, asset, set } = route.useSearch()
  return (
    <Suspense fallback={<SkelPageLoading label="Opening Split to bones…" />}>
      <SplitToBonesPage imageId={id} setId={set} assetUid={asset} />
    </Suspense>
  )
}
